import styled, { css } from 'styled-components';
import {
  SPARKLINE_TONE_DEFAULT,
  type SparklineTone,
} from './Sparkline.constants';

const TONE_COLOR: Record<
  SparklineTone,
  (theme: { colors?: Record<string, string | undefined> }) => string
> = {
  default: ({ colors = {} }) => colors.primary ?? '#C1C1FF',
  primary: ({ colors = {} }) => colors.primary ?? '#C1C1FF',
  success: ({ colors = {} }) => colors.success ?? '#8CD88C',
  caution: ({ colors = {} }) => colors.caution ?? '#FFDF9E',
  error: ({ colors = {} }) => colors.error ?? '#FFB4AB',
  // `info` prefers a dedicated `colors.info` when the consumer's theme
  // exposes one, falling back to `primary` (most M3 themes don't include
  // an `info` key) so the chart still has a sensible tint instead of
  // being indistinguishable from `primary`.
  info: ({ colors = {} }) => colors.info ?? colors.primary ?? '#C1C1FF',
};

const resolveToneColor = (
  tone: SparklineTone,
  theme: { colors?: Record<string, string | undefined> },
): string => (TONE_COLOR[tone] ?? TONE_COLOR[SPARKLINE_TONE_DEFAULT])(theme);

interface RootProps {
  $height: number;
  $gap: number;
  $tone: SparklineTone;
}

/**
 * The chart container is `position: relative` so the single floating
 * tooltip — see `<Tip>` — can position itself in the same coordinate
 * space as the bars. We render one tooltip that moves to the active bar
 * rather than N tooltips because (a) it scales to long timelines without
 * blowing up the DOM and (b) it eliminates the flicker that comes with
 * adjacent-trigger handoff.
 */
export const Root = styled.div<RootProps>`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: stretch;
  width: 100%;
  ${({ $height, $gap }) => css`
    height: ${$height}px;
    gap: ${$gap}px;
  `}
  ${({ $tone, theme }) => css`
    color: ${resolveToneColor($tone, theme)};
  `}
`;

/**
 * Per-bar height + corner radius are passed via inline `style` rather than
 * styled-component props because they vary per render: a 30-bin chart would
 * otherwise force styled-components to generate (and inject) up to 30
 * uniquely-hashed classes every render. Inline style is one DOM attribute
 * write per bar, no stylesheet churn.
 *
 * `Bar` is the static (non-interactive) shape — used when `interactive` is
 * false. Interactive charts use `BarButton` below.
 *
 * `color: inherit` is explicit (rather than relying on inheritance) so the
 * bar always picks up the tone colour set on `Root`, even when wrapped in
 * an element that overrides `color` (e.g. a heading).
 */
export const Bar = styled.span`
  flex: 1 1 0;
  min-width: 1px;
  display: block;
  color: inherit;
  background: currentColor;
  opacity: 0.9;
  transition: opacity 120ms ease;
`;

/**
 * Interactive variant: a real `<button>` so it gets keyboard focus, Enter /
 * Space activation, and screen-reader role for free. The active bar (set on
 * hover or focus) brightens slightly so the link between the tooltip and
 * its source bar is unambiguous.
 *
 * `color: inherit` is critical here — `<button>` carries a UA-supplied
 * `color: buttontext` that does NOT inherit from the parent, so without
 * this override `background: currentColor` would resolve to the browser's
 * default button text colour (black-ish) instead of the tone colour set
 * on `Root`.
 */
export const BarButton = styled.button<{ $isActive: boolean }>`
  flex: 1 1 0;
  min-width: 1px;
  display: block;
  padding: 0;
  border: 0;
  color: inherit;
  background: currentColor;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  opacity: ${({ $isActive }) => ($isActive ? '1' : '0.85')};
  transition: opacity 120ms ease, transform 120ms ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }
`;

/**
 * The single floating tooltip. Visually mirrors the `Tooltip` atom (same
 * elevation, same border, same typography) so the two read as a family.
 * Positioned via inline `style.left` so it slides to the active bar without
 * generating a new class per index.
 */
export const Tip = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 6px);
  transform: translate(-50%, ${({ $isOpen }) => ($isOpen ? '0' : '-2px')});
  width: max-content;
  max-width: 220px;
  padding: 6px 10px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.small ?? '8px'};
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  pointer-events: none;
  z-index: 1500;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transition: opacity 120ms
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
    transform 120ms
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};

  ${({ theme: { colors = {}, elevation = {} } }) => css`
    background: ${colors.surfaceContainer ?? '#1F1F27'};
    color: ${colors.onSurface ?? '#E4E1ED'};
    border: 1px solid ${colors.outlineVariant ?? '#464555'};
    box-shadow: ${elevation.level2 ??
    '0 1px 2px 0 rgb(0 0 0 / 0.30), 0 2px 6px 2px rgb(0 0 0 / 0.15)'};
  `}
`;

export const TipLabel = styled.div`
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
`;

export const TipValue = styled.div`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 13px;
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurface ?? '#E4E1ED'};
  `}
`;
